import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface ContentAnalysis {
  wordCount: number
  characterCount: number
  characterCountNoSpaces: number
  paragraphCount: number
  sentenceCount: number
  readingTimeMinutes: number
  headingStructure: Array<{ level: number; text: string; position: number }>
  keywordDensity: Map<string, number>
  readabilityScore?: number
  recommendations: string[]
}

interface SEOScore {
  score: number
  maxScore: number
  percentage: number
  issues: string[]
  suggestions: string[]
}

interface Conflict {
  field: string
  label: string
  yours: string
  theirs: string
  resolution?: 'yours' | 'theirs' | 'custom'
  customValue?: string
}
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { format } from 'date-fns'
import { CalendarIcon, X, History, ChartBar, FileText, AlertCircle, AlertTriangle, BookOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiService } from '@/services/api'
import { toast } from 'sonner'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { useDrawer } from '@/components/ui/drawer'
// import { useAuth } from '@/contexts/AuthContext'
import { BlogEditor } from './editor/BlogEditor'
import { imageUploadHandler } from './editor/ImageUploadHandler'
import { BlogPostPreview } from './BlogPostPreview'
import { BlogPostRevisions } from './BlogPostRevisions'
// import { BlogEditPresence } from './BlogEditPresence'
import { useEditLock } from '@/hooks/useEditLock'
import { BlogConflictResolver } from './BlogConflictResolver'
import { SEOPreviews } from './SEOPreviews'
import { CharacterCounter } from '@/components/ui/character-counter'
import { calculateSEOScore, getSEOScoreColor, getSEOScoreLabel } from '@/utils/seoScoring'
import { analyzeContent, getReadabilityLabel, getReadabilityColor } from '@/utils/contentAnalysis'
import DOMPurify from 'dompurify'
import { useBlogMetadataContext } from '@/contexts/BlogMetadataContext'
import { ImagePlaceholderSection } from './ImagePlaceholderSection'

interface BlogTemplate {
  id: string
  name: string
  description?: string
  title_template?: string
  content_template: string
  excerpt_template?: string
  categories: Array<{ id: string; name: string; color?: string }>
  tags: Array<{ id: string; name: string }>
}

/**
 * Helper function to format SEO warning codes into user-friendly messages
 */
function formatSEOWarning(warning: string): { message: string; severity: 'error' | 'warning'; icon: typeof AlertCircle } {
  // title_too_long_72 -> "SEO title is too long (72 characters, max 65 recommended)"
  if (warning.startsWith('title_too_long_')) {
    const length = warning.split('_').pop()
    return {
      message: `SEO title is too long (${length} characters, max 65 recommended)`,
      severity: 'warning',
      icon: AlertTriangle,
    }
  }

  // description_too_long_180 -> "Meta description is too long (180 characters, max 155 recommended)"
  if (warning.startsWith('description_too_long_')) {
    const length = warning.split('_').pop()
    return {
      message: `Meta description is too long (${length} characters, max 155 recommended)`,
      severity: 'warning',
      icon: AlertTriangle,
    }
  }

  // keyword_density_low_0.5 -> "Keyword density is low (0.5%, target 1-2%)"
  if (warning.startsWith('keyword_density_low_')) {
    const density = warning.split('_').pop()
    return {
      message: `Keyword density is low (${density}%, target 1-2%)`,
      severity: 'warning',
      icon: AlertCircle,
    }
  }

  // h1_found_2 -> "Found 2 H1 headings in content (should be 0, title is the only H1)"
  if (warning.startsWith('h1_found_')) {
    const count = warning.split('_').pop()
    return {
      message: `Found ${count} H1 heading${parseInt(count || '0') > 1 ? 's' : ''} in content (removed - title is the only H1)`,
      severity: 'error',
      icon: AlertCircle,
    }
  }

  // readability_low_45.2 -> "Readability score is low (45.2/100, target 60+)"
  if (warning.startsWith('readability_low_')) {
    const score = warning.split('_').pop()
    return {
      message: `Readability score is low (${score}/100, target 60+ for general audience)`,
      severity: 'warning',
      icon: BookOpen,
    }
  }

  // Default fallback
  return {
    message: warning.replace(/_/g, ' '),
    severity: 'warning',
    icon: AlertCircle,
  }
}

interface BlogPostFormDrawerProps {
  post?: {
    id: string
    slug: string
    title: string
    content: string
    excerpt?: string
    status: 'draft' | 'review' | 'published' | 'scheduled'
    published_at?: string
    scheduled_at?: string
    seo_title?: string
    seo_description?: string
    seo_keywords?: string
    og_title?: string
    og_description?: string
    og_image?: string
    twitter_title?: string
    twitter_description?: string
    twitter_image?: string
    featured_image?: string
    reading_time_minutes?: number
    version?: number
    last_modified_at?: string
    categories: Array<{ id: string; name: string; color?: string }>
    tags: Array<{ id: string; name: string }>
    ai_metadata?: {
      phase?: string
      seo_warnings?: string[]
      readability_score?: number
      word_count?: number
      image_suggestions?: Array<{
        position: number
        alt_text: string
        placeholder: string
        caption: string
        type: 'chart' | 'diagram' | 'photo' | 'infographic' | 'screenshot'
        context: string
        uploaded_url?: string
      }>
    }
  }
  onClose: () => void
  onFormStateChange?: (state: {
    loading: boolean
    isSaving: boolean
    lastSaved: Date | null
    hasUnsavedChanges: boolean
    handleSubmit: (action: 'draft' | 'review' | 'publish' | 'schedule') => Promise<void>
    status: 'draft' | 'review' | 'published' | 'scheduled'
  }) => void
}

export const BlogPostFormDrawer: React.FC<BlogPostFormDrawerProps> = ({ post, onClose, onFormStateChange }) => {
  const { sendMessage } = useDrawerMessaging('blog-post-form')
  const { openDrawer, closeDrawer } = useDrawer()
  // const { user } = useAuth()
  const { categories: allCategories, tags: allTags } = useBlogMetadataContext()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('content')
  const autoSaveEnabled = true // Can be made configurable later
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [showConflictResolver, setShowConflictResolver] = useState(false)
  const [conflicts] = useState<Conflict[]>([])
  const [currentVersion] = useState(post?.version || 1)
  const [contentAnalysis, setContentAnalysis] = useState<ContentAnalysis | null>(null)
  const [seoScore, setSeoScore] = useState<SEOScore | null>(null)
  const originalDataRef = useRef<typeof formData | null>(null)
  const lockAcquiredRef = useRef(false)
  const [templates, setTemplates] = useState<BlogTemplate[]>([])
  const [, setLoadingTemplates] = useState(false)

  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    content: post?.content || '',
    excerpt: post?.excerpt || '',
    status: post?.status || 'draft' as 'draft' | 'review' | 'published' | 'scheduled',
    seo_title: post?.seo_title || '',
    seo_description: post?.seo_description || '',
    seo_keywords: post?.seo_keywords || '',
    og_title: post?.og_title || '',
    og_description: post?.og_description || '',
    og_image: post?.og_image || '',
    twitter_title: post?.twitter_title || '',
    twitter_description: post?.twitter_description || '',
    twitter_image: post?.twitter_image || '',
    featured_image: post?.featured_image || '',
    reading_time_minutes: post?.reading_time_minutes || 5,
    category_ids: post?.categories?.map(c => c.id) || [] as string[],
    tag_ids: post?.tags?.map(t => t.id) || [] as string[],
    published_at: post?.published_at ? new Date(post.published_at) : null,
    scheduled_at: post?.scheduled_at ? new Date(post.scheduled_at) : null,
  })

  // Edit lock management
  const { acquireLock } = useEditLock(
    post?.id || '',
    apiService
  )

  // Unsaved changes hook
  useUnsavedChanges({
    hasChanges: hasUnsavedChanges,
    message: 'You have unsaved changes. Are you sure you want to leave?'
  })

  // Store original data for comparison
  useEffect(() => {
    originalDataRef.current = { ...formData }
  }, [formData])

  // Check for unsaved changes
  useEffect(() => {
    if (originalDataRef.current) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalDataRef.current)
      setHasUnsavedChanges(hasChanges)
    }
  }, [formData])

  // Analyze content when it changes
  useEffect(() => {
    if (formData.content) {
      const analysis = analyzeContent(formData.content)
      setContentAnalysis(analysis)
    }
  }, [formData.content])

  // Calculate SEO score when relevant fields change
  useEffect(() => {
    const score = calculateSEOScore({
      title: formData.seo_title || formData.title,
      description: formData.seo_description || formData.excerpt,
      content: formData.content,
      keywords: formData.seo_keywords,
      slug: formData.slug,
      featuredImage: formData.featured_image,
      excerpt: formData.excerpt,
    })
    setSeoScore(score)
  }, [formData.title, formData.seo_title, formData.seo_description, formData.content, formData.slug, formData.excerpt, formData.featured_image, formData.seo_keywords])

  useEffect(() => {
    imageUploadHandler.createBucketIfNotExists()
  }, [])

  useEffect(() => {
    if (!post?.id || lockAcquiredRef.current) return

    lockAcquiredRef.current = true
    acquireLock()

    return () => {
      lockAcquiredRef.current = false
    }
  }, [post?.id, acquireLock])

  const generateSlug = useCallback((title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }, [])

  const handleAutoSave = useCallback(async () => {
    if (formData.status === 'published') return
    if (!hasUnsavedChanges) return

    setIsSaving(true)
    try {
      const payload = {
        ...formData,
        status: 'draft' as const,
        published_at: formData.published_at?.toISOString() || null,
        scheduled_at: formData.scheduled_at?.toISOString() || null,
        slug: formData.slug || generateSlug(formData.title),
      }

      if (post) {
        await apiService.blog.posts.update(post.id, payload)

        // Create an auto-save revision
        await apiService.blog.revisions.create(post.id, {
          revision_notes: 'Auto-save revision',
          is_autosave: true
        })
      } else {
        await apiService.blog.posts.create(payload)
      }

      setLastSaved(new Date())
      setHasUnsavedChanges(false)
      originalDataRef.current = formData
    } catch (err) {
      console.error('Auto-save failed:', err)
      toast.error('Auto-save failed')
    } finally {
      setIsSaving(false)
    }
  }, [formData, hasUnsavedChanges, post, generateSlug])

  useEffect(() => {
    if (autoSaveEnabled && formData.title && formData.content) {
      const autoSaveTimer = setTimeout(() => {
        handleAutoSave()
      }, 30000)
      return () => clearTimeout(autoSaveTimer)
    }
  }, [formData, autoSaveEnabled, handleAutoSave])

  const handleSubmit = useCallback(async (action: 'draft' | 'review' | 'publish' | 'schedule') => {
    const errors: Record<string, string> = {}

    if (!formData.title.trim()) {
      errors.title = 'Title is required'
    }

    if (!formData.content.trim()) {
      errors.content = 'Content is required'
    }

    if (action === 'schedule' && !formData.scheduled_at) {
      errors.scheduled_at = 'Schedule date is required'
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      toast.error('Please fix validation errors before submitting')
      setActiveTab('content')
      return
    }

    setValidationErrors({})
    setLoading(true)

    try {
      const status = action === 'draft' ? 'draft'
        : action === 'review' ? 'review'
        : action === 'publish' ? 'published'
        : 'scheduled'

      const payload = {
        ...formData,
        status,
        published_at: status === 'published' ? new Date().toISOString() : (formData.published_at?.toISOString() || null),
        scheduled_at: formData.scheduled_at?.toISOString() || null,
        slug: formData.slug || generateSlug(formData.title),
      }

      if (post) {
        await apiService.blog.posts.update(post.id, payload)
        toast.success('Blog post updated successfully')
      } else {
        await apiService.blog.posts.create(payload)
        toast.success('Blog post created successfully')
      }

      sendMessage({ to: 'blog-posts-page', action: 'refresh' })
      onClose()
    } catch (err) {
      console.error('Failed to save blog post:', err)
      toast.error('Failed to save blog post')
    } finally {
      setLoading(false)
    }
  }, [formData, generateSlug, post, sendMessage, onClose])

  // Notify parent of form state changes
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange({
        loading,
        isSaving,
        lastSaved,
        hasUnsavedChanges,
        handleSubmit,
        status: formData.status,
      })
    }
  }, [loading, isSaving, lastSaved, hasUnsavedChanges, handleSubmit, formData.status, onFormStateChange])

  const toggleCategory = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }))
  }

  const toggleTag = (tagId: string) => {
    setFormData(prev => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter(id => id !== tagId)
        : [...prev.tag_ids, tagId]
    }))
  }

  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const url = await imageUploadHandler.uploadImage(file)
      toast.success('Image uploaded successfully')
      return url
    } catch (error) {
      toast.error('Failed to upload image')
      throw error
    }
  }

  const handleAutoPopulateSEO = () => {
    setFormData(prev => ({
      ...prev,
      seo_title: prev.seo_title || prev.title.slice(0, 60),
      seo_description: prev.seo_description || prev.excerpt.slice(0, 160) || DOMPurify.sanitize(prev.content, { ALLOWED_TAGS: [] }).slice(0, 160),
      og_title: prev.og_title || prev.title,
      og_description: prev.og_description || prev.excerpt || DOMPurify.sanitize(prev.content, { ALLOWED_TAGS: [] }).slice(0, 160),
      og_image: prev.og_image || prev.featured_image,
      twitter_title: prev.twitter_title || prev.title,
      twitter_description: prev.twitter_description || prev.excerpt || DOMPurify.sanitize(prev.content, { ALLOWED_TAGS: [] }).slice(0, 160),
      twitter_image: prev.twitter_image || prev.featured_image,
    }))
    toast.success('SEO fields populated from content')
  }


  const handleApplyTemplate = (template: BlogTemplate) => {
    setFormData(prev => ({
      ...prev,
      title: template.title_template || prev.title,
      content: template.content_template,
      excerpt: template.excerpt_template || prev.excerpt,
      category_ids: template.categories.map(c => c.id),
      tag_ids: template.tags.map(t => t.id),
    }))
    toast.success(`Template "${template.name}" applied successfully`)
  }

  const handleOpenTemplatePicker = async () => {
    // Fetch templates first if not already loaded
    let templatesToShow = templates
    if (templates.length === 0) {
      setLoadingTemplates(true)
      try {
        const response = await apiService.blog.templates.list()
        templatesToShow = response as BlogTemplate[]
        setTemplates(templatesToShow)
      } catch (error) {
        console.error('Failed to fetch templates:', error)
        toast.error('Failed to load templates')
        setLoadingTemplates(false)
        return
      }
      setLoadingTemplates(false)
    }

    const drawerId = 'template-picker'
    openDrawer({
      id: drawerId,
      title: 'Choose a Template',
      content: (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select a template to populate your post with pre-configured content, categories, and tags.
          </p>
          {templatesToShow.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {templatesToShow.map((template) => (
                <Card
                  key={template.id}
                  className="bg-card text-card-foreground border-border hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    handleApplyTemplate(template)
                    closeDrawer(drawerId)
                  }}
                >
                  <div className="p-4 space-y-3">
                    <div>
                      <h3 className="font-semibold text-foreground">{template.name}</h3>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {template.description}
                        </p>
                      )}
                    </div>

                    {/* Categories & Tags */}
                    {(template.categories.length > 0 || template.tags.length > 0) && (
                      <div className="space-y-2">
                        {template.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {template.categories.map((category) => (
                              <Badge
                                key={category.id}
                                variant="outline"
                                style={category.color ? { borderColor: category.color } : {}}
                                className="text-xs"
                              >
                                {category.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {template.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {template.tags.map((tag) => (
                              <Badge
                                key={tag.id}
                                variant="secondary"
                                className="text-xs"
                              >
                                {tag.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border">
                      <span>
                        {template.title_template ? 'With title template' : 'Content only'}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs hover:bg-primary/10"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleApplyTemplate(template)
                          closeDrawer(drawerId)
                        }}
                      >
                        Use Template
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-lg font-medium text-foreground">No templates available</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Create templates in the Templates page to use them here
              </p>
            </div>
          )}
        </div>
      ),
      size: 'lg',
    })
  }

  const wordCount = formData.content ? DOMPurify.sanitize(formData.content, { ALLOWED_TAGS: [] }).split(/\s+/).filter(w => w).length : 0
  const readingTime = Math.ceil(wordCount / 200)

  return (
    <>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="metadata">Metadata</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
          <TabsTrigger value="revisions">
            <History className="w-4 h-4 mr-1" />
            History
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <ChartBar className="w-4 h-4 mr-1" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto">
          <TabsContent value="content" className="space-y-4 p-4">
            {/* Template Picker Button */}
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleOpenTemplatePicker}
                disabled={loading}
                className="border-border hover:bg-accent"
              >
                <FileText className="h-4 w-4 mr-2" />
                Use Template
              </Button>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <Label htmlFor="title" className={validationErrors.title ? 'text-red-600' : ''}>Title *</Label>
                <CharacterCounter
                  current={formData.title.length}
                  max={200}
                  warning={160}
                />
              </div>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value })
                  if (!post && !formData.slug) {
                    setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))
                  }
                  if (validationErrors.title) {
                    setValidationErrors(prev => ({ ...prev, title: '' }))
                  }
                }}
                placeholder="Enter post title"
                disabled={loading}
                maxLength={200}
                className={validationErrors.title ? 'border-red-500 focus:ring-red-500' : ''}
              />
              {validationErrors.title && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.title}</p>
              )}
            </div>

            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="post-url-slug"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty to auto-generate from title
              </p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <Label htmlFor="excerpt">Excerpt</Label>
                <CharacterCounter
                  current={formData.excerpt.length}
                  max={160}
                  warning={140}
                  showRemaining
                />
              </div>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brief description of the post (appears in search results and previews)"
                rows={3}
                disabled={loading}
                maxLength={160}
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className={validationErrors.content ? 'text-red-600' : ''}>Content *</Label>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{contentAnalysis?.wordCount || 0} words</span>
                  <span>•</span>
                  <span>{contentAnalysis?.readingTimeMinutes || 0} min read</span>
                  {(isSaving || lastSaved) && (
                    <>
                      <span>•</span>
                      <span className={isSaving ? 'text-blue-600 font-medium animate-pulse' : 'text-green-600'}>
                        {isSaving ? 'Saving...' : `Saved ${format(lastSaved!, 'h:mm a')}`}
                      </span>
                    </>
                  )}
                  {hasUnsavedChanges && !isSaving && (
                    <>
                      <span>•</span>
                      <span className="text-amber-600 font-medium">Unsaved changes</span>
                    </>
                  )}
                </div>
              </div>
              <BlogEditor
                content={formData.content}
                onChange={(content) => {
                  setFormData({ ...formData, content })
                  if (validationErrors.content) {
                    setValidationErrors(prev => ({ ...prev, content: '' }))
                  }
                }}
                onImageUpload={handleImageUpload}
                disabled={loading}
              />
              {validationErrors.content && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.content}</p>
              )}
            </div>

            <div>
              <Label htmlFor="featured_image">Featured Image URL</Label>
              <Input
                id="featured_image"
                value={formData.featured_image}
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                placeholder="https://example.com/image.jpg"
                disabled={loading}
              />
            </div>
          </TabsContent>

          <TabsContent value="metadata" className="space-y-4 p-4">
            <div>
              <Label>Categories</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {allCategories.map((category) => (
                  <Badge
                    key={category.id}
                    variant={formData.category_ids.includes(category.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    style={category.color ? { borderColor: category.color } : {}}
                    onClick={() => toggleCategory(category.id)}
                  >
                    {category.name}
                    {formData.category_ids.includes(category.id) && (
                      <X className="ml-1 h-3 w-3" />
                    )}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {allTags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant={formData.tag_ids.includes(tag.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleTag(tag.id)}
                  >
                    {tag.name}
                    {formData.tag_ids.includes(tag.id) && (
                      <X className="ml-1 h-3 w-3" />
                    )}
                  </Badge>
                ))}
              </div>
            </div>

            {/* AI-Generated SEO Warnings */}
            {post?.ai_metadata?.seo_warnings && post.ai_metadata.seo_warnings.length > 0 && (
              <Card className="bg-card text-card-foreground border-border p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <h4 className="font-semibold text-foreground">AI SEO Analysis</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    The AI detected the following SEO issues during content generation:
                  </p>
                  <div className="space-y-2">
                    {post.ai_metadata.seo_warnings.map((warning, index) => {
                      const formatted = formatSEOWarning(warning)
                      const Icon = formatted.icon
                      return (
                        <div
                          key={index}
                          className={cn(
                            'flex items-start gap-3 p-3 rounded-lg border',
                            formatted.severity === 'error'
                              ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
                              : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
                          )}
                        >
                          <Icon
                            className={cn(
                              'h-4 w-4 mt-0.5 flex-shrink-0',
                              formatted.severity === 'error'
                                ? 'text-red-600 dark:text-red-500'
                                : 'text-amber-600 dark:text-amber-500'
                            )}
                          />
                          <span
                            className={cn(
                              'text-sm',
                              formatted.severity === 'error'
                                ? 'text-red-900 dark:text-red-200'
                                : 'text-amber-900 dark:text-amber-200'
                            )}
                          >
                            {formatted.message}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                  {post.ai_metadata.readability_score && (
                    <div className="pt-2 border-t border-border text-xs text-muted-foreground">
                      <strong>Readability Score:</strong> {post.ai_metadata.readability_score}/100
                    </div>
                  )}
                  {post.ai_metadata.word_count && (
                    <div className="text-xs text-muted-foreground">
                      <strong>Word Count:</strong> {post.ai_metadata.word_count.toLocaleString()} words
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* AI Image Suggestions (BLOG-AI-007) */}
            {post?.ai_metadata?.image_suggestions && post.ai_metadata.image_suggestions.length > 0 && (
              <ImagePlaceholderSection
                imageSuggestions={post.ai_metadata.image_suggestions}
                onImageUpload={(_position, file) => handleImageUpload(file)}
                disabled={loading}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: 'draft' | 'review' | 'published' | 'scheduled') =>
                    setFormData({ ...formData, status: value })
                  }
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="reading_time">Reading Time (minutes)</Label>
                <Input
                  id="reading_time"
                  type="number"
                  min={1}
                  value={readingTime}
                  disabled
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-calculated: {wordCount} words
                </p>
              </div>
            </div>

            {formData.status === 'published' && (
              <div>
                <Label>Publish Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !formData.published_at && 'text-muted-foreground'
                      )}
                      disabled={loading}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.published_at ? (
                        format(formData.published_at, 'PPP')
                      ) : (
                        <span>Select date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.published_at || undefined}
                      onSelect={(date) => setFormData({ ...formData, published_at: date || null })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {formData.status === 'scheduled' && (
              <div>
                <Label>Schedule Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !formData.scheduled_at && 'text-muted-foreground'
                      )}
                      disabled={loading}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.scheduled_at ? (
                        format(formData.scheduled_at, 'PPP')
                      ) : (
                        <span>Select date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.scheduled_at || undefined}
                      onSelect={(date) => setFormData({ ...formData, scheduled_at: date || null })}
                      initialFocus
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </TabsContent>

          <TabsContent value="seo" className="space-y-4 p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">SEO Settings</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAutoPopulateSEO}
                disabled={loading}
              >
                Auto-populate from content
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label htmlFor="seo_title">SEO Title</Label>
                  <CharacterCounter
                    current={formData.seo_title.length}
                    max={60}
                    warning={50}
                  />
                </div>
                <Input
                  id="seo_title"
                  value={formData.seo_title}
                  onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                  placeholder="Custom title for search engines (shows in search results)"
                  disabled={loading}
                  maxLength={60}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label htmlFor="seo_description">SEO Description</Label>
                  <CharacterCounter
                    current={formData.seo_description.length}
                    max={160}
                    warning={140}
                  />
                </div>
                <Textarea
                  id="seo_description"
                  value={formData.seo_description}
                  onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                  placeholder="Meta description for search engines (appears below title in search results)"
                  rows={3}
                  maxLength={160}
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="seo_keywords">SEO Keywords</Label>
                <Input
                  id="seo_keywords"
                  value={formData.seo_keywords}
                  onChange={(e) => setFormData({ ...formData, seo_keywords: e.target.value })}
                  placeholder="keyword1, keyword2, keyword3"
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Comma-separated list of keywords
                </p>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium">Open Graph (Facebook)</h4>

              <div>
                <Label htmlFor="og_title">OG Title</Label>
                <Input
                  id="og_title"
                  value={formData.og_title}
                  onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                  placeholder="Title for social sharing"
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="og_description">OG Description</Label>
                <Textarea
                  id="og_description"
                  value={formData.og_description}
                  onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                  placeholder="Description for social sharing"
                  rows={2}
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="og_image">OG Image URL</Label>
                <Input
                  id="og_image"
                  value={formData.og_image}
                  onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                  placeholder="https://example.com/og-image.jpg"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium">Twitter Card</h4>

              <div>
                <Label htmlFor="twitter_title">Twitter Title</Label>
                <Input
                  id="twitter_title"
                  value={formData.twitter_title}
                  onChange={(e) => setFormData({ ...formData, twitter_title: e.target.value })}
                  placeholder="Title for Twitter cards"
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="twitter_description">Twitter Description</Label>
                <Textarea
                  id="twitter_description"
                  value={formData.twitter_description}
                  onChange={(e) => setFormData({ ...formData, twitter_description: e.target.value })}
                  placeholder="Description for Twitter cards"
                  rows={2}
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="twitter_image">Twitter Image URL</Label>
                <Input
                  id="twitter_image"
                  value={formData.twitter_image}
                  onChange={(e) => setFormData({ ...formData, twitter_image: e.target.value })}
                  placeholder="https://example.com/twitter-image.jpg"
                  disabled={loading}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="preview" className="p-4">
            <BlogPostPreview
              title={formData.title}
              content={formData.content}
              excerpt={formData.excerpt}
              featuredImage={formData.featured_image}
              categories={allCategories.filter(c => formData.category_ids.includes(c.id))}
              tags={allTags.filter(t => formData.tag_ids.includes(t.id))}
              publishedAt={formData.published_at || new Date()}
              readingTime={readingTime}
            />
          </TabsContent>

          {/* Revisions Tab */}
          {post?.id && (
            <TabsContent value="revisions" className="p-4">
              <BlogPostRevisions
                postId={post.id}
                currentVersion={currentVersion}
                onRestore={() => {
                  // Refresh the post data after restore
                  sendMessage({ to: 'parent', action: 'refresh' })
                  toast.success('Post restored successfully')
                }}
              />
            </TabsContent>
          )}

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="p-4 space-y-6">
            {/* Content Analysis */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Content Analysis</h3>
              {contentAnalysis && (
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4">
                    <h4 className="font-medium mb-2">Statistics</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Words:</span>
                        <span className="font-medium">{contentAnalysis.wordCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Characters:</span>
                        <span className="font-medium">{contentAnalysis.characterCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Paragraphs:</span>
                        <span className="font-medium">{contentAnalysis.paragraphCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sentences:</span>
                        <span className="font-medium">{contentAnalysis.sentenceCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Reading Time:</span>
                        <span className="font-medium">{contentAnalysis.readingTimeMinutes} min</span>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <h4 className="font-medium mb-2">Readability</h4>
                    {contentAnalysis.readabilityScore !== undefined && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Score:</span>
                          <Badge className={getReadabilityColor(contentAnalysis.readabilityScore)}>
                            {contentAnalysis.readabilityScore}/100
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Level:</span>
                          <span className={cn("font-medium", getReadabilityColor(contentAnalysis.readabilityScore))}>
                            {getReadabilityLabel(contentAnalysis.readabilityScore)}
                          </span>
                        </div>
                      </div>
                    )}
                  </Card>
                </div>
              )}

              {/* Content Recommendations */}
              {contentAnalysis?.recommendations && contentAnalysis.recommendations.length > 0 && (
                <Card className="p-4">
                  <h4 className="font-medium mb-2">Recommendations</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {contentAnalysis.recommendations.map((rec: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>

            {/* SEO Analysis */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">SEO Analysis</h3>
              {seoScore && (
                <>
                  <Card className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium">SEO Score</h4>
                      <div className="flex items-center gap-2">
                        <span className={cn("text-2xl font-bold", getSEOScoreColor(seoScore.percentage))}>
                          {seoScore.percentage}%
                        </span>
                        <Badge className={getSEOScoreColor(seoScore.percentage)}>
                          {getSEOScoreLabel(seoScore.percentage)}
                        </Badge>
                      </div>
                    </div>

                    <div className="w-full bg-muted rounded-full h-2 mb-4">
                      <div
                        className={cn("h-2 rounded-full transition-all", {
                          "bg-red-500": seoScore.percentage < 40,
                          "bg-orange-500": seoScore.percentage >= 40 && seoScore.percentage < 60,
                          "bg-yellow-500": seoScore.percentage >= 60 && seoScore.percentage < 80,
                          "bg-green-500": seoScore.percentage >= 80,
                        })}
                        style={{ width: `${seoScore.percentage}%` }}
                      />
                    </div>

                    {/* Issues */}
                    {seoScore.issues.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-medium mb-2 text-red-600">Issues to Fix:</h5>
                        <ul className="space-y-1 text-sm">
                          {seoScore.issues.map((issue: string, index: number) => (
                            <li key={index} className="flex items-start text-red-600">
                              <X className="w-4 h-4 mr-2 mt-0.5" />
                              <span>{issue}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Suggestions */}
                    {seoScore.suggestions.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium mb-2">Suggestions:</h5>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          {seoScore.suggestions.map((suggestion: string, index: number) => (
                            <li key={index} className="flex items-start">
                              <span className="mr-2">💡</span>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Card>

                  {/* SEO Previews */}
                  <SEOPreviews
                    title={formData.seo_title || formData.title}
                    description={formData.seo_description || formData.excerpt}
                    url={`https://joulescrm.com/blog/${formData.slug}`}
                    image={formData.og_image || formData.featured_image}
                  />
                </>
              )}
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Conflict Resolver Dialog */}
      <BlogConflictResolver
        isOpen={showConflictResolver}
        conflicts={conflicts}
        onResolve={(resolutions) => {
          // Apply resolved conflicts
          const resolved: Record<string, unknown> = {}
          resolutions.forEach((conflict) => {
            if (conflict.resolution === 'yours') {
              resolved[conflict.field] = conflict.yours
            } else if (conflict.resolution === 'theirs') {
              resolved[conflict.field] = conflict.theirs
            } else {
              resolved[conflict.field] = conflict.customValue || conflict.yours
            }
          })
          setFormData(prev => ({ ...prev, ...resolved }))
          setShowConflictResolver(false)
          toast.success('Conflicts resolved successfully')
        }}
        onCancel={() => setShowConflictResolver(false)}
      />
    </>
  )
}